import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Building2, User, Mail, Phone, MapPin, Lock, Eye, EyeOff, Loader2, Shield, CreditCard, AlertCircle } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { auth, functions, db } from '../firebase';
import BrandLogo from '../components/BrandLogo';
import { useBrandSettings } from '../contexts/BrandSettingsContext';

const SignupFlowPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [error, setError] = useState('');
  const { brandName } = useBrandSettings();
  const [showPassword, setShowPassword] = useState(false);
  const [plans, setPlans] = useState([]);

  const planFromUrl = searchParams.get('plan');

  const [formData, setFormData] = useState({
    // Step 1: Plan Selection
    plan: planFromUrl || 'professional',
    // Step 2: Company Information
    companyName: '',
    industry: '',
    employees: '',
    country: 'South Africa',
    // Step 3: Account Creation
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    password: '',
    confirmPassword: ''
  });

  // Fetch plans from Platform Pricing document (same as PricingPage)
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);

        // Pull packages from /platform/pricing document
        const pricingRef = doc(db, 'platform', 'pricing');
        const pricingSnap = await getDoc(pricingRef);

        if (pricingSnap.exists() && pricingSnap.data().packages) {
          const fetchedPlans = pricingSnap.data().packages;
          // Sort by sortOrder if available
          fetchedPlans.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

          // Transform to match expected format for the wizard
          const transformedPlans = fetchedPlans.map(pkg => ({
            id: pkg.id,
            name: pkg.name,
            price: pkg.price,
            currency: 'USD',
            priceZAR: pkg.priceZAR || Math.round(pkg.price * 18), // Fallback conversion
            popular: pkg.isPopular || false,
            features: pkg.displayFeatures || pkg.features || []
          }));

          setPlans(transformedPlans);

          // If planFromUrl doesn't match any fetched plan, default to first plan
          if (planFromUrl && !transformedPlans.find(p => p.id === planFromUrl)) {
            setFormData(prev => ({ ...prev, plan: transformedPlans[0]?.id || 'professional' }));
          }
        } else {
          console.error('Platform pricing not found');
          setPlansError('Pricing configuration not found. Please contact support.');
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setPlansError('Failed to load pricing plans. Please try again.');
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [planFromUrl]);

  const industries = [
    'Certification Body',
    'Inspection Body',
    'Testing Laboratory',
    'Calibration Laboratory',
    'Training Provider',
    'Consulting Firm',
    'Accreditation Body',
    'Registrar',
    'Notified Body',
    'Conformity Assessment Body',
    'Other'
  ];

  const employeeSizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  const handleInputChange = (field, value) => {
    if (field === 'email') {
      value = value.toLowerCase().trim();
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        // Must have loaded plans and selected a valid one
        return !plansLoading && !plansError && formData.plan !== '' && plans.some(p => p.id === formData.plan);
      case 2:
        return formData.companyName && formData.industry && formData.employees;
      case 3:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          return false;
        }
        if (formData.password.length < 8) {
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          return false;
        }
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const getValidationError = (step) => {
    switch (step) {
      case 1:
        if (plansLoading) {
          return 'Please wait for plans to load';
        }
        if (plansError) {
          return 'Unable to load plans. Please refresh the page.';
        }
        if (!formData.plan || !plans.some(p => p.id === formData.plan)) {
          return 'Please select a plan';
        }
        return null;
      case 3:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
          return 'Please fill in all required fields';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          return 'Please enter a valid email address';
        }
        if (formData.password.length < 8) {
          return 'Password must be at least 8 characters';
        }
        if (formData.password !== formData.confirmPassword) {
          return 'Passwords do not match';
        }
        return null;
      default:
        return 'Please fill in all required fields';
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      setError('');
    } else {
      setError(getValidationError(currentStep) || 'Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSignup = async () => {
    setLoading(true);
    setError('');

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email || !emailRegex.test(formData.email.trim())) {
        throw new Error('Please enter a valid email address.');
      }

      const displayName = `${formData.firstName} ${formData.lastName}`;

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email.trim(),
          formData.password
        );
        await updateProfile(userCredential.user, { displayName });
      } catch (authErr) {
        if (authErr.code === 'auth/email-already-in-use') {
          throw new Error('This email is already registered. Please sign in or use a different email.');
        } else if (authErr.code === 'auth/weak-password') {
          throw new Error('Password is too weak. Please use at least 8 characters.');
        } else if (authErr.code === 'auth/invalid-email') {
          throw new Error('Please enter a valid email address.');
        }
        throw authErr;
      }

      // Initialize user profile and organization
      try {
        const initUserProfile = httpsCallable(functions, 'initializeUserProfile');
        await initUserProfile();

        const initOrganization = httpsCallable(functions, 'initializeOrganization');
        await initOrganization({
          name: formData.companyName,
          industry: formData.industry,
          country: formData.country,
          plan: formData.plan
        });
      } catch (initErr) {
        console.error('Initialization error:', initErr);
        // Continue anyway - user is created
      }

      setCurrentStep(5);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.plan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/10 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <BrandLogo />
          </Link>
          <p className="text-muted-foreground">Start your 14-day free trial</p>
        </div>

        {/* Progress Bar */}
        {currentStep < 5 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step ? <Check size={20} /> : step}
                  </div>
                  {step < 4 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      currentStep > step ? 'bg-primary' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Plan</span>
              <span>Company</span>
              <span>Account</span>
              <span>Confirm</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* Step 1: Plan Selection */}
              {currentStep === 1 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
                  <p className="text-muted-foreground mb-8">Select the plan that best fits your organization's needs</p>

                  {/* Loading state */}
                  {plansLoading && (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="ml-3 text-muted-foreground">Loading plans...</span>
                    </div>
                  )}

                  {/* Error state */}
                  {plansError && !plansLoading && (
                    <div className="flex flex-col items-center justify-center py-16">
                      <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                      <p className="text-red-500 mb-4">{plansError}</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                      >
                        Try Again
                      </button>
                    </div>
                  )}

                  {/* Plans grid */}
                  {!plansLoading && !plansError && plans.length > 0 && (
                    <div className={`grid gap-6 ${
                      plans.length === 3 ? 'md:grid-cols-3' :
                      plans.length === 2 ? 'md:grid-cols-2' :
                      'md:grid-cols-1 max-w-md mx-auto'
                    }`}>
                      {plans.map((plan) => (
                        <div
                          key={plan.id}
                          onClick={() => handleInputChange('plan', plan.id)}
                          className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                            formData.plan === plan.id
                              ? 'border-primary bg-primary/5 shadow-lg'
                              : 'border-gray-200 hover:border-primary/50'
                          }`}
                        >
                          {plan.popular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-3 py-1 rounded-full text-xs font-semibold">
                              Most Popular
                            </div>
                          )}

                          <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                          <div className="mb-4">
                            <span className="text-3xl font-bold text-primary">${plan.price}</span>
                            <span className="text-muted-foreground">/month</span>
                            {plan.priceZAR && (
                              <div className="text-sm text-muted-foreground">R{plan.priceZAR.toLocaleString()}/month</div>
                            )}
                          </div>

                          <ul className="space-y-2 mb-6">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start text-sm text-gray-700">
                                <Check size={16} className="text-primary mr-2 mt-0.5 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>

                          {formData.plan === plan.id && (
                            <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                              <Check size={16} className="text-white" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Company Information */}
              {currentStep === 2 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Company Information</h2>
                  <p className="text-muted-foreground mb-8">Tell us about your organization</p>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          value={formData.companyName}
                          onChange={(e) => handleInputChange('companyName', e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Acme Corporation"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Organization Type *
                        </label>
                        <select
                          value={formData.industry}
                          onChange={(e) => handleInputChange('industry', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select organization type</option>
                          {industries.map((industry) => (
                            <option key={industry} value={industry}>{industry}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Employees *
                        </label>
                        <select
                          value={formData.employees}
                          onChange={(e) => handleInputChange('employees', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select size</option>
                          {employeeSizes.map((size) => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Account Creation */}
              {currentStep === 3 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Your Account</h2>
                  <p className="text-muted-foreground mb-8">Set up your login credentials</p>

                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="John"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Smith"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone (Optional)
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="+27 12 345 6789"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password *
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Min 8 characters"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm Password *
                        </label>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Confirm password"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {currentStep === 4 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Start Your Free Trial</h2>
                  <p className="text-muted-foreground mb-8">Review your details and start your 14-day trial</p>

                  <div className="max-w-lg mx-auto">
                    {/* Free Trial Banner */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <Check size={24} className="text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-green-800 text-lg">14-Day Free Trial</h3>
                          <p className="text-green-700 text-sm">No credit card required</p>
                        </div>
                      </div>
                      <p className="text-green-700 text-sm">
                        Get full access to all features. Cancel anytime during your trial.
                      </p>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-8">
                      <h3 className="font-semibold text-gray-900 mb-4">Summary</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Plan:</span>
                          <span className="font-medium text-gray-900">{selectedPlan?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Company:</span>
                          <span className="font-medium text-gray-900">{formData.companyName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Email:</span>
                          <span className="font-medium text-gray-900">{formData.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Trial Period:</span>
                          <span className="font-medium text-green-600">14 days free</span>
                        </div>
                        <div className="border-t pt-3 mt-3">
                          <div className="flex justify-between text-lg">
                            <span className="font-semibold text-gray-900">After trial:</span>
                            <span className="font-bold text-primary">${selectedPlan?.price}/month</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Security badges */}
                    <div className="flex items-center justify-center gap-6 mb-8 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Shield size={18} className="text-green-600" />
                        <span>256-bit SSL</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CreditCard size={18} className="text-green-600" />
                        <span>Secure Signup</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Success */}
              {currentStep === 5 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={40} className="text-green-600" />
                  </div>

                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to {brandName}!</h2>
                  <p className="text-lg text-muted-foreground mb-8">
                    Your 14-day free trial has started!
                    <br />
                    <span className="text-primary font-medium">Your account is ready to use.</span>
                  </p>

                  <div className="bg-primary/5 rounded-lg p-6 mb-8 max-w-md mx-auto">
                    <h3 className="font-semibold text-gray-900 mb-4">Your Account Details</h3>
                    <div className="space-y-2 text-left text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Organization:</span>
                        <span className="font-medium text-gray-900">{formData.companyName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Plan:</span>
                        <span className="font-medium text-gray-900">{selectedPlan?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email:</span>
                        <span className="font-medium text-gray-900">{formData.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Trial ends:</span>
                        <span className="font-medium text-green-600">In 14 days</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => window.location.href = 'https://isocore-portal.web.app'}
                      className="w-full max-w-md px-8 py-4 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-semibold text-lg"
                    >
                      Go to Dashboard →
                    </button>
                    <p className="text-sm text-muted-foreground">
                      You're already logged in. Click above to access your dashboard.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {currentStep < 5 && (
                <div className="flex justify-between mt-8 pt-6 border-t">
                  {currentStep > 1 ? (
                    <button
                      onClick={prevStep}
                      className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <ArrowLeft size={20} />
                      Back
                    </button>
                  ) : (
                    <div />
                  )}

                  {currentStep < 4 ? (
                    <button
                      onClick={nextStep}
                      className="flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium"
                    >
                      Continue
                      <ArrowRight size={20} />
                    </button>
                  ) : (
                    <button
                      onClick={handleSignup}
                      disabled={loading}
                      className="flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        <>
                          Start Free Trial
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupFlowPage;
